<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue'
import { useFetch } from '@vueuse/core'
import { changeLocale } from '@formkit/vue'
import LottieAnimation from 'lottie-vuejs/src/LottieAnimation.vue'
import MultistepAttendee from '@/components/Form/MultistepAttendee.vue'
import MultistepOrder from '@/components/Form/MultistepOrder.vue'
import MultistepInvoice from '@/components/Form/MultistepInvoice.vue'
import MultistepConfirmation from '@/components/Form/MultistepConfirmation.vue'
import { useToken } from '@/composables/token'
import { getStoredGtmData } from '@/directives/gtm-helper.js'

const props = defineProps({
  event: {
    type: String,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  data: {
    type: String,
    required: true
  },
  earlyArrival: {
    type: String,
  }
})

const currentLanguage = ref('de')
const submitting = ref(false)
const submitted = ref(false)
const submitError = ref(false)
const stepError = ref(false)

// This is used to honyepot bots
const userId = ref(null)

const parsedSchema = (() => {
  try {
    return JSON.parse(props.data)
  } catch (error) {
    console.error('Unable to parse schema', error)
    return []
  }
})()

const steps = ref(Array.isArray(parsedSchema) ? parsedSchema : [])
const formSteps = computed(() => steps.value.filter((step) => step.id !== 'confirmation'))
const confirmationStep = computed(() => steps.value.find((step) => step.id === 'confirmation') || null)
const currentStepIndex = ref(0)

const formData = reactive({})
const { getCSRFToken } = useToken()

const currentStep = computed(() => formSteps.value[currentStepIndex.value] || null)
const attendeeStep = computed(() => formSteps.value.find((step) => step.id === 'attendees') || null)
const orderStep = computed(() => formSteps.value.find((step) => step.id === 'order_details') || null)
const invoiceStep = computed(() => formSteps.value.find((step) => step.id === 'invoice_details') || null)
const totalSteps = computed(() => steps.value.length)

const stepProgress = computed(() => {
  if (!totalSteps.value) {
    return '0/0'
  }

  const activeStepNumber = submitted.value
    ? totalSteps.value
    : Math.min(currentStepIndex.value + 1, formSteps.value.length)

  return `${activeStepNumber}/${totalSteps.value}`
})

const canGoBack = computed(() => currentStepIndex.value > 0)
const isLastStep = computed(() => currentStepIndex.value === formSteps.value.length - 1)

const nextLabel = computed(() => 'Nächster Schritt')
const previousLabel = computed(() => 'Zurück')
const submitLabel = computed(() => 'Anmeldung absenden')

const stepErrorMessage = computed(() => 'Bitte füllen Sie alle Pflichtfelder aus, bevor Sie fortfahren.')
const submitErrorMessage = computed(() => 'Es ist ein Fehler beim Absenden aufgetreten. Bitte versuchen Sie es erneut.')

onMounted(async () => {
  changeLocale('de')
  await nextTick()

  const element = document.querySelector('.app-multistep-form')
  element.addEventListener('click', formStartedEvent)
})

const formStartedEvent = () => {
  const formStarted = JSON.parse(localStorage.getItem('formStarted'))

  if (formStarted) {
    return
  }

  localStorage.setItem('formStarted', 'true')
  // Get the gtm Data from localStorage
  const data = getStoredGtmData()

  if (!window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  };

  // Also check utm Params from sessionStorage
  const utmParams = sessionStorage.getItem('utm_params')

  if (utmParams) {
    data.utmParams = JSON.parse(utmParams)
  }

  window.dataLayer.push({
    event: 'form_start',
    ...data
  });

  gtmFormEvent('form_step_1')
}

const updateFormData = (name, value) => {
  formData[name] = value
  if (stepError.value) {
    stepError.value = false
  }
  if (submitError.value) {
    submitError.value = false
  }
}

const goToNextStep = async () => {
  const valid = await validateStep()

  if (isLastStep.value || !valid) {
    return
  }

  currentStepIndex.value += 1
  // need to increase the index once more for real world index
  gtmFormEvent(`form_step_${currentStepIndex.value + 1}`)
}

const goToPreviousStep = () => {
  if (!canGoBack.value) {
    return
  }
  currentStepIndex.value -= 1
  stepError.value = false
}

const validateStep = async () => {
  if (!currentStep.value) {
    return false
  }
  const steps = currentStep.value.elements

  for (const step of steps) {
    if (step.required && !formData[step.name]) {
      stepError.value = true
      return false
    }
  }

  return true
}

const handleSubmit = async () => {
  const valid = await validateStep()
  if (!valid) {
    return
  }

  if (userId.value && userId.value.length > 0) {
    alert('You are a bot!')
    return
  }
  submitError.value = false
  submitting.value = true

  const utmParams = sessionStorage.getItem('utm_params')

  if (utmParams) {
    formData.utmParams = utmParams
  }

  // add Event-Id to formData
  formData.event = props.event

  const { data, error } = await useFetch(`${window.location.origin}/api/v1/submit-form`, {
    async beforeFetch({ options, cancel }) {
      const { token, error: tokenError } = await getCSRFToken()
      if (tokenError || !token) {
        submitError.value = true
        submitting.value = false
        cancel()
        console.error(tokenError)
        return tokenError
      }
      options.headers = {
        ...options.headers,
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      }
      return { options }
    }
  }).post({
    webform_id: props.type,
    values: formData
  }).json()

  if (error.value) {
    submitError.value = true
    submitting.value = false
    return error.value
  }

  if (data.value?.ok) {
    submitting.value = false
    submitError.value = false
    submitted.value = true

    if (window.lintrk) {
      window.lintrk('track', { conversion_id: 28782689 });
    }

    gtmFormEvent('purchased')
    return
  }

  submitError.value = true
  submitting.value = false
}

const gtmFormEvent = (formEvent) => {
  // Get the gtm Data from localStorage
  const data = getStoredGtmData()

  if (!window.dataLayer) {
    window.dataLayer = window.dataLayer || [];
  };

  // Also check utm Params from sessionStorage
  const utmParams = sessionStorage.getItem('utm_params')

  if (utmParams) {
    data.utmParams = JSON.parse(utmParams)
  }
  window.dataLayer.push({
    event: formEvent,
    ...data
  });
}

const handleStepSubmit = async () => {
  await handleSubmit()
}
</script>

<template>
  <div class="app-multistep-form max-w-screen-2xl mx-auto px-6">
    <div
      v-if="!submitting && !submitted && formSteps.length"
      class="multistep-inner"
    >
        <FormKit
          type="form"
          form-class="my-20"
          :id="props.type"
          :actions="false"
          :incompleteMessage="false"
        >
          <MultistepAttendee
            v-if="currentStep?.id === 'attendees' && attendeeStep"
            :existingAttendees="Array.isArray(formData.attendees) ? formData.attendees : []"
            :data="attendeeStep"
            :event="event"
            :can-go-back="canGoBack"
            :is-last-step="isLastStep"
            :next-label="nextLabel"
            :previous-label="previousLabel"
            :submit-label="submitLabel"
            :step-error="stepError"
            :step-error-message="stepErrorMessage"
            :earlyArrival="earlyArrival"
            @updateValue="updateFormData"
            @back="goToPreviousStep"
            @next="goToNextStep"
            @submitStep="handleStepSubmit"
          />
          <MultistepOrder
            v-if="currentStep?.id === 'order_details' && orderStep"
            :data="orderStep"
            :values="formData"
            :can-go-back="canGoBack"
            :is-last-step="isLastStep"
            :next-label="nextLabel"
            :previous-label="previousLabel"
            :submit-label="submitLabel"
            :step-error="stepError"
            :step-error-message="stepErrorMessage"
            @updateValue="updateFormData"
            @back="goToPreviousStep"
            @next="goToNextStep"
            @submitStep="handleStepSubmit"
          />
          <MultistepInvoice
            v-if="currentStep?.id === 'invoice_details' && invoiceStep"
            :data="invoiceStep"
            :values="formData"
            :can-go-back="canGoBack"
            :is-last-step="isLastStep"
            :next-label="nextLabel"
            :previous-label="previousLabel"
            :submit-label="submitLabel"
            :step-error="stepError"
            :step-error-message="stepErrorMessage"
            @updateValue="updateFormData"
            @back="goToPreviousStep"
            @next="goToNextStep"
            @submitStep="handleStepSubmit"
          />
          <input
            v-model="userId"
            name="user_id"
            autocomplete="off"
            class="visually-hidden opacity-0"
            type="text"
          />
          <div class="step-headline">
            <p
              class="step-progress text-primary ~text-3xl/6xl font-light"
            >
              {{ stepProgress }}
            </p>
          </div>
        <p
          v-if="submitError"
          class="submit-error-message"
        >
          {{ submitErrorMessage }}
        </p>
      </FormKit>
    </div>
    <div
      v-else-if="submitting"
      class="multistep-inner"
    >
      <GridLayout hasGap>
        <p
          class="step-progress text-primary ~text-3xl/6xl font-light"
        >
          {{ stepProgress }}
        </p>
        <div class="lg:col-span-8 flex items-center justify-center">
          <LottieAnimation
            path="./themes/custom/eyedea/assets/lottie/circular-loader.json"
            :auto-play="true"
            :loop="true"
            :height="400"
            :width="400"
          />
        </div>
      </GridLayout>
    </div>
    <div
      v-else-if="submitted"
      class="multistep-inner"
    >
      <MultistepConfirmation
        v-if="confirmationStep"
        :data="confirmationStep"
      />
      <p
        v-else
        class="submit-success-fallback"
      >
        Vielen Dank. Ihre Anmeldung wurde erfolgreich gesendet.
      </p>
      <div class="step-headline">
        <p
          class="step-progress text-primary ~text-3xl/6xl font-light"
        >
          {{ stepProgress }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="multistep-error flex flex-col py-10 gap-4"
    >
      <p
        class="multistep-error-fallback font-bold text-lg mb-0"
      >
        Das Formular wird in Kürze zur Verfügung stehen.
      </p>
      <p
        class="multistep-error-fallback text-lg mb-0"
      >
        Bitte melden Sie sich in der Zwischenzeit per E-Mail an.
      </p>
      <a
        class="multistep-error text-primary text-lg underline mb-0"
        href="mailto:post@team-rosenkranz.de"
      >
        post@team-rosenkranz.de
      </a>
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-multistep-form {
  .step-headline {
    @apply flex flex-col lg:flex-row lg:items-end lg:justify-between mb-8;
  }

  .step-markup {
    @apply mb-6 text-headline;
    :deep(h2) {
       @apply ~text-2xl/5xl;
    }

    :deep(p) {
      @apply mb-3;
    }
  }

  .submit-error-message {
    @apply mt-6 text-danger ~text-lg/xl;
  }

  .submit-success-fallback {
    @apply my-10 text-headline ~text-xl/3xl;
  }

}
</style>
